<template>
    <div class="slice job-page__tips">
        <div class="slice-content">
            <article class="job-page__tips__article">
                <header class="job-page__tips__header">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div class="job-page__tips__title" v-html="richTitle" />
                    <div class="social-sharing">
                        <button aria-label="Share on Facebook" @click="shareOnFacebook"><VJoyIcon name="facebook" /></button>
                        <button aria-label="Share on Twitter" @click="shareOnTwitter"><VJoyIcon name="twitter" /></button>
                        <button aria-label="Share on LinkedIn" @click="shareOnLinkedIn"><VJoyIcon name="linkedin" /></button>
                    </div>
                </header>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="job-page__tips__content" v-html="richContent" />
            </article>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {VJoyIcon} from '@maltjoy/core-vue';
    import type {LandingPageContent} from '@api';
    import socialSharingActions from '~/composables/socialSharingActions';

    const props = defineProps<{page: LandingPageContent; title: string; content: string}>();

    const {shareOnFacebook, shareOnTwitter, shareOnLinkedIn} = socialSharingActions();
    const enrichContent = (content: string) => content.replaceAll('<a ', '<a class="link" ');
    const richTitle = enrichContent(props.title);
    const richContent = enrichContent(props.content);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $article-max-width: 940px;
    $article-offset-mobile: var(--joy-core-spacing-8);
    $article-offset-desktop: var(--joy-core-spacing-12);

    .job-page__tips {
        &__article {
            text-align: left;
            max-width: $article-max-width;
            margin: 0 auto;
        }

        &__content {
            @include mq.screen_xs(min) {
                padding-left: $article-offset-mobile;
            }

            @include mq.screen_md(min) {
                padding-left: $article-offset-desktop;
            }
        }

        &__header {
            @include mq.screen_md(min) {
                display: flex;
                flex-direction: row-reverse;
                justify-content: flex-start;
            }
        }

        .social-sharing {
            display: flex;
            margin-top: var(--joy-core-spacing-4);
            gap: var(--joy-core-spacing-2);

            @include mq.screen_md(min) {
                flex-direction: column;
                flex: none;
                width: $article-offset-desktop;
                margin-top: 0;
                gap: var(--joy-core-spacing-1);
            }

            // width: var(--joy-core-spacing-6);
            button {
                transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                padding: 2px;

                &:hover {
                    opacity: 0.5;
                }
            }
        }

        h2,
        h3,
        h4 {
            font-family: var(--joy-font-family-title);
            line-height: var(--joy-line-height-small);
            font-weight: var(--joy-font-weight-normal);
        }

        h2 {
            font-size: var(--joy-font-size-secondary-200);
            margin-bottom: var(--joy-core-spacing-10);

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-secondary-600);
                margin-bottom: var(--joy-core-spacing-12);
            }
        }

        h3 {
            position: relative;
            margin-bottom: 1em;
            font-size: var(--joy-font-size-primary-800);

            @include mq.screen_xs(min) {
                counter-increment: title;

                &::before {
                    position: absolute;
                    content: counter(title) '.';
                    color: var(--joy-color-primary-50);
                    left: calc(-1 * $article-offset-mobile);
                }
            }

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-secondary-400);

                &::before {
                    font-size: var(--joy-font-size-secondary-600);
                    top: -0.1em;
                    left: calc(-1 * $article-offset-desktop);
                }
            }

            &:not(:first-child) {
                margin-top: 2em;
            }
        }

        h4 {
            margin-bottom: 0.4em;
            font-size: var(--joy-font-size-primary-600);

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-secondary-200);
            }
        }

        ul,
        ol {
            margin-left: 1.5em;
            margin-bottom: 0.83em;
        }

        p {
            margin-bottom: 1.9em;
        }
    }
</style>
